import { ComponentProps, FC, ReactNode } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { BaseText, Password } from '@trustycars/genie-ui/components/hook-fields'

import { loginFormPlaceholderText } from '$constants/texts'
import { TypeSubmitForm } from '$hook-forms/types'
import {
  requiredString,
  requiredTrimmedEmail
} from '$hook-forms/general-validators'

import StatusMessage from '$elements/StatusMessage'
import { submit } from '$forms/common/style.css'

import { container } from './styles.css'

const Schema = z.object({
  email: requiredTrimmedEmail,
  password: requiredString
})
const resolver = zodResolver(Schema)

type TFields = z.infer<typeof Schema>

const defaultValues: TFields = {
  email: '',
  password: ''
}

export type LoginFormFields = TFields

export type LoginFormProps = TypeSubmitForm<TFields> & {
  errorMessage?: ComponentProps<typeof StatusMessage>['children']
  postFooter?: ReactNode
}

export const LoginForm: FC<LoginFormProps> = ({
  onSuccess,
  disabled,
  postFooter,
  errorMessage
}) => {
  const { control, reset, handleSubmit, formState } = useForm<TFields>({
    resolver,
    defaultValues,
    mode: 'all'
  })

  const isInvalid = disabled || !!Object.keys(formState.errors).length

  return (
    <form
      className={container}
      onSubmit={handleSubmit((...handler) => onSuccess(reset, ...handler))}
      noValidate
      autoComplete="off"
    >
      {errorMessage && (
        <StatusMessage kind="danger">{errorMessage}</StatusMessage>
      )}

      <BaseText
        control={control}
        name="email"
        label="Email"
        inputProps={{ placeholder: loginFormPlaceholderText.email }}
        required
        disabled={disabled}
      />
      <Password
        control={control}
        name="password"
        label="Password"
        required
        disabled={disabled}
      />

      <button
        name="login"
        type="submit"
        className={submit}
        disabled={isInvalid}
      >
        Log in
      </button>

      {postFooter}
    </form>
  )
}

export default LoginForm
